/**
 * Billing Service for Phoenix Electoral Analytics
 *
 * Handles:
 * - Stripe Checkout session creation
 * - Transaction history
 * - Invoice download
 */

import { getIdTokenHeaders } from './authHeaders';

const API_URL = process.env.REACT_APP_API_URL || 'https://moje-volebni-analyza-178765449659.europe-west1.run.app';

/**
 * Transaction record from backend
 */
export interface Transaction {
  id: string;
  user_id: string;
  amount_czk: number;
  credits_purchased: number;
  status: string;
  invoice_number: string | null;
  created_at: string;
  stripe_session_id: string | null;
}

/**
 * Checkout session response
 */
export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

/**
 * Session verification response
 */
export interface SessionVerificationResponse {
  session_id: string;
  payment_status: string;
  credits_purchased: number;
  amount_total: number;
}

/**
 * Create a Stripe Checkout session and redirect to payment page
 */
export const createCheckoutSession = async (
  user: { uid: string; email: string | null; displayName: string | null },
  credits: number = 1
): Promise<CheckoutSessionResponse> => {
  const headers = await getIdTokenHeaders();
  const response = await fetch(`${API_URL}/api/billing/create-checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      credits,
      user_email: user.email || '',
      user_name: user.displayName || '',
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || 'Failed to create checkout session');
  }

  return response.json();
};

/**
 * Get transaction history for current user
 */
export const getTransactions = async (
  user: { uid: string; email: string | null; displayName: string | null }
): Promise<Transaction[]> => {
  const headers = await getIdTokenHeaders();
  const response = await fetch(`${API_URL}/api/billing/transactions`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || 'Failed to fetch transactions');
  }

  const data = await response.json();
  return data.transactions;
};

/**
 * Download invoice PDF
 */
export const downloadInvoice = async (
  user: { uid: string; email: string | null; displayName: string | null },
  transactionId: string,
  invoiceNumber: string
): Promise<void> => {
  const headers = await getIdTokenHeaders();
  const response = await fetch(`${API_URL}/api/billing/invoice/${transactionId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || 'Failed to download invoice');
  }

  // Create download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Verify checkout session after redirect from Stripe.
 * Force-refreshes the token because the user may have spent 20+ min on the Stripe page.
 */
export const verifySession = async (
  user: { uid: string; email: string | null; displayName: string | null },
  sessionId: string
): Promise<SessionVerificationResponse> => {
  // Force-refresh: token may be stale after long Stripe checkout flow
  const headers = await getIdTokenHeaders(true);
  const response = await fetch(`${API_URL}/api/billing/verify-session/${sessionId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || 'Failed to verify session');
  }

  return response.json();
};

/**
 * Format date for Czech locale
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format amount in CZK
 */
export const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
